import { storage } from '@pukka/storage';

// Must be serializeable!
type StorageOptions<T extends Record<string, unknown>> = {
  initialValue?: T;
  session?: string;
  serializer?: {
    [t in keyof T]?: typeof JSON.stringify;
  };
  parser?: {
    [t in keyof T]?: typeof JSON.parse;
  };
  expiry?: number;
};

function storageCreator<T extends Record<string, unknown>>(key: string, options?: StorageOptions<T>) {
  return {
    get<U extends keyof T>(localKey: U): T[U] | undefined {
      const item = storage.get(`${key}/${localKey as string}`);
      const expiry = storage.get(`${key}/${localKey as string}/expiry`) ?? '0';
      const parser = options?.parser?.[localKey] ?? JSON.parse;

      if (item !== null) {
        if (Number(expiry) > Date.now()) {
          return parser(item);
        }

        // Clean the key and expiry
        storage.remove(`${key}/${localKey as string}`);
        storage.remove(`${key}/${localKey as string}/expiry`);
      }

      return options?.initialValue?.[localKey] ?? undefined;
    },
    set<U extends keyof T>(localKey: U, value: T[U]) {
      const serializer = options?.serializer?.[localKey] ?? JSON.stringify;
      const expiry = options?.expiry ?? 86_400_000; // 1 day default

      storage.set(`${key}/${localKey as string}/expiry`, String(Date.now() + expiry), {
        session: options?.session,
      });

      return storage.set(`${key}/${localKey as string}`, serializer(value), {
        session: options?.session,
      });
    },
  };
}

export type { StorageOptions };

export { storageCreator };
